import React from "react";

/**
 * A one-size-fits-all button that automatically adopts the
 * Nextra sidebar link colours on hover.
 */
type ButtonProps = {
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  /** marks the button as “selected” (= sticky active) */
  selected?: boolean;
  accent?: boolean;
  children: React.ReactNode;
};

const Button: React.FC<ButtonProps> = ({
  onClick,
  disabled,
  className,
  selected,
  accent,
  children,
}) => {
  /* ───────────────────────────────────────── base styles ── */
  const base =
    "self-start inline-flex items-center gap-2 text-xs sm:text-sm " +
    "rounded p-2 border shadow transition-colors duration-200 " +
    "border-primary dark:border-[#323232]";

  const shadow = "shadow dark:shadow-[0_3px_6px_-1.5px_rgba(150,150,150,0.1)]";

  /* ───────────────────────────────────────── state styles ─ */
  const disabledStyles = "opacity-40 cursor-default";

  const selectedStyles =
    "opacity-100 cursor-default !border-[var(--primary-accent-color)] " +
    "!bg-[var(--primary-background-color)]";

  const hoverStyles =
    // Light & dark share the same CSS variables, so one set of rules is enough
    "hover:border-[color:var(--pluto-status-blue)] " +
    "hover:bg-[color:var(--color-secondary-accent)] " +
    "dark:hover:border-[color:var(--pluto-status-blue)] " +
    "cursor-pointer";

  const activeStyles = selected ? selectedStyles : hoverStyles;

  const combined = [
    base,
    shadow,
    disabled ? disabledStyles : activeStyles,
    className ?? "",
  ].join(" ");

  return (
    <button onClick={onClick} disabled={disabled} className={combined}>
      {children}
    </button>
  );
};

export default Button;
